import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * GAVN custom (2026-09-21) — "Fri fragt-filler" i kurven.
 *
 * Lægger det foreslåede produkt (den variant der får kunden over fri
 * fragt-grænsen) i kurven med ét /cart/update.js-kald og lader kurvens egne
 * komponenter re-rendere via CartLinesUpdateEvent-promisen — samme mønster som
 * bundle-upsell.js / product-form.js. Bruger cart_update_url (ikke add.js), så
 * svaret er hele kurven og createCartFromAjaxResponse virker.
 *
 * Markup: snippets/free-shipping-filler.liquid.
 * SKAL GENINDSÆTTES EFTER EN TEMAOPDATERING (denne fil, snippet'en, script-tag
 * i scripts.liquid og placeringen i cart-summary.liquid).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [button]
 * @property {HTMLElement} [liveRegion]
 * @extends {Component<Refs>}
 */
class FreeShippingFillerComponent extends Component {
  #busy = false;

  /**
   * Lægger den foreslåede variant i kurven.
   * @param {Event} [event]
   */
  async add(event) {
    event?.preventDefault();
    if (this.#busy) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    this.refs.button?.setAttribute('disabled', '');

    // Alle kurvsektioner på siden (drawer + evt. kurvside) skal refreshes.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({
        updates: {
          [variantId]: 1,
        },
        sections: sectionIds,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status || !response.ok) {
        throw new Error(data.description || data.message || 'Kurven kunne ikke opdateres');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          items: data.items,
          source: 'free-shipping-filler-component',
          sourceId: this.id ? this.id.toString() : undefined,
          sections: data.sections,
          didError: false,
        },
      });
      // Ved succes morfer kurvsektionen og kortet genberegnes/forsvinder,
      // så knappen behøver ikke gen-aktiveres.
    } catch (error) {
      deferred.reject(error);
      document.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Tilføjelse fejlede',
          code: 'FILLER_ADD_FAILED',
        })
      );
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = 'Kunne ikke tilføje. Prøv igen.';
      }
      this.refs.button?.removeAttribute('disabled');
      this.removeAttribute('aria-busy');
      this.#busy = false;
    }
  }
}

if (!customElements.get('free-shipping-filler-component')) {
  customElements.define('free-shipping-filler-component', FreeShippingFillerComponent);
}
